import { MediaSlot, MethodologyTypeEnum } from "./../util/enum";
import { store } from "@/commonui/store/index";
import { VuexModule, Module, Mutation, Action, getModule } from "vuex-module-decorators";
import { cloneDeep } from "lodash";
import { ContentExposureService } from "@/services/content-exposure";
import { deepClone } from "@/util/func";
import { TeachingActivityService } from "@/services/teaching-activity";
import { ContainerType, SignatureResoureModel } from "@/services/admin";

function toTimeString(second) {
	const sec = second % 60;
	return `${Math.floor(second / 60)}:${sec > 9 ? sec : `0${sec}`}`;
}
function toSeconds(time: string) {
	const times = time.split(":");
	return parseInt(times[0]) * 60 + parseInt(times[1]);
}
function getEnumKV(enumType) {
	const kvs = Object.keys(enumType)
		.filter((key) => Number.isNaN(parseInt(key)))
		.map((key) => ({ key: key.toLowerCase(), value: enumType[key] }));
	return {
		keys: () => kvs.map((kv) => kv.key),
		kvs: () => kvs.map((kv) => ({ ...kv }))
	};
}
@Module({ namespaced: true, name: "ContentExposureState", store: store, dynamic: true })
class ContentExposureState extends VuexModule {
	versionGroupId: string = "";
	contentId: string = "";
	vcsLevelId: string = "";
	contentExposures: any[] = [];
	methodologyDifference = {
		classic: 0,
		remote: 0,
		digital: 0,
		independent: 0
	};
	teachingActivityService = TeachingActivityService();
	signatures: SignatureResoureModel[] = [];
	defaultMediaSlot = MediaSlot.ViewingContent;

	@Mutation
	setContentVersionGroupId({ versionGroupId, contentId, vcsLevelId }) {
		this.versionGroupId = versionGroupId;
		this.contentId = contentId;
		this.vcsLevelId = vcsLevelId;
	}

	@Mutation
	setContentId(contentId) {
		this.contentId = contentId;
	}

	@Mutation
	setContentExposures(contentExposures: any[]) {
		this.contentExposures = contentExposures;
	}

	@Mutation
	setContentExposure(contentExposure: any) {
		const index = this.contentExposures.findIndex((d) => d.id === contentExposure.id);
		const data = cloneDeep(this.contentExposures[index]);
		Object.getOwnPropertyNames(contentExposure).forEach((key) => {
			data[key] = contentExposure[key];
		});
		this.contentExposures.splice(index, 1, data);
	}
	@Action
	createExposure({ lessonPlanVersionId, currentSequence, numberOfExposures, currentMediaSlot, isDigitalOrRemoteLP }) {
		return ContentExposureService.create({
			contentId: this.contentId,
			minPlanningTime: toSeconds("3:00"),
			maxPlanningTime: toSeconds("3:30"),
			sequence: currentSequence,
			numberOfExposures: numberOfExposures,
			lessonPlanVersionId: lessonPlanVersionId,
			isDigitalOrRemoteLessonPlanVersion: isDigitalOrRemoteLP,
			contentExposureMediaSlot: currentMediaSlot ? [{ mediaSlotId: currentMediaSlot }] : []
		});
	}

	@Action
	removeExposure({ id }) {
		return ContentExposureService.deleteContentExposure({ id });
	}

	@Action
	cloneExposure({ recordIds, targetLessonPlanVersionId }) {
		return ContentExposureService.duplicateContentExposure(recordIds, targetLessonPlanVersionId);
	}

	@Action
	setMediaSlot({ record: { id }, values }) {
		this.setContentExposure({ id, mediaSlots: [values.target.value] });
	}

	@Action
	setDuration({ record: { id }, values }) {
		this.setContentExposure({ id, duration: values });
	}

	@Action
	setMethodology({ record: { id }, values }) {
		this.setContentExposure({ id, methodologies: values });
	}

	@Action
	setRadioMethodology({ record: { id }, values }) {
		this.setContentExposure({ id, methodologies: [values.target.value] });
	}

	@Action
	setRelatedItems({ record: { id }, values }) {
		this.setContentExposure({ id, relatedItems: values });
	}

	@Action
	setTeachingActivity({ record: { id }, values }) {
		this.setContentExposure({ id, teachingActivities: values });
	}

	@Action
	setExposureTeachingActivity({ record: { id }, values }) {
		this.setContentExposure({ id, contentExposureTeachingActivity: values });
	}

	@Action
	setObjectives({ record: { id }, values }) {
		this.setContentExposure({ id, objectives: values });
	}

	@Action
	async addExposure({
		lessonPlanVersionId,
		currentSequence,
		numberOfExposures,
		currentMediaSlot,
		isDigitalOrRemoteLP
	}) {
		const result = await this.createExposure({
			lessonPlanVersionId,
			currentSequence,
			numberOfExposures,
			currentMediaSlot,
			isDigitalOrRemoteLP
		});
		await this.fetchContentExposures(lessonPlanVersionId);
		return result;
	}

	@Action
	editingExposure({ id, isEdit }) {
		this.setContentExposure({ id, isEdit });
	}

	@Action
	async editExposure({ record, isDigitalOrRemoteLP }) {
		record = await this.createCustomActivites(record);
		return ContentExposureService.update(
			((d) => {
				d.minPlanningTime = toSeconds(d.duration.start);
				d.maxPlanningTime = toSeconds(d.duration.end);
				d.contentExposureRelatedItem = d.relatedItems.map((item) => ({
					...item
				}));
				d.contentExposureTeachingActivity = d.teachingActivities.map((item) => ({
					contentExposureId: d.id,
					teachingActivityId: item.id,
					sequence: item.sequence,
					teachingActivity: item,
					pageId: item.pageId,
					imageName: item.imageName,
					metaData: item.metaData,
					objectiveId: item.objectiveId
				}));
				d.contentExposureObjective = d.objectives.map((item) => ({
					contentExposureId: item.contentExposureId || d.id,
					contentObjective: item.contentObjective,
					sequence: item.sequence,
					contentObjectiveId: item.contentObjectiveId || item.contentObjective.id
				}));
				d.contentExposureMediaSlot = d.mediaSlots.map((m) => ({
					mediaSlotId: m
				}));
				d.isExposureEdit = true;
				d.isDigitalOrRemoteLessonPlanVersion = isDigitalOrRemoteLP;
				delete d.duration;
				delete d.isEdit;
				delete d.key;
				delete d.methodologies;
				delete d.methodologySums;
				delete d.objectives;
				delete d.relatedItems;
				delete d.teachingActivities;
				delete d.mediaSlots;
				d.contentExposureObjective?.forEach((v) => {
					delete v.contentObjective;
				});
				d.contentExposureTeachingActivity?.forEach((v) => {
					delete v.teachingActivity;
				});
				return d;
			})(deepClone(record))
		);
	}

	@Action
	async createCustomActivites(record) {
		const allActivities = deepClone(record.teachingActivities);
		const customActivitiesToBeAdded = allActivities.filter((f) => f.isNew);
		// add the custom activities to db
		if (customActivitiesToBeAdded.length) {
			try {
				const { item1, item2 } = await this.teachingActivityService.createMultipleTeachingActivity(
					customActivitiesToBeAdded
				);
				// update the selected activities
				const updatedActivities = allActivities.map((f) => {
					const updatedItem = item1.find((i) => i.text === f.text);
					if (updatedItem) {
						f.id = updatedItem?.id;
						f.contentExposures = [];
						delete f.isNew;
					}
					return f;
				});
				record.teachingActivities = updatedActivities;
			} catch (e) {
				// show error
			}
		}
		return record;
	}

	@Action({ rawError: true })
	async copyExposure({ recordIds, targetLessonPlanVersionId, lessonPlanVersionId }) {
		const result = await this.cloneExposure({ recordIds, targetLessonPlanVersionId });
		await this.fetchContentExposures(lessonPlanVersionId);
		return result;
	}

	@Action
	async deleteExposure(record) {
		await this.removeExposure(record);
		await this.fetchContentExposures(record.lessonPlanVersionId);
	}

	@Action
	async updateExposure({ record, isDigitalOrRemoteLP }) {
		await this.editExposure({ record, isDigitalOrRemoteLP });
		await this.fetchContentExposures(record.lessonPlanVersionId);
	}

	@Action
	async fetchContentExposures(lessonPlanVersionId?: string) {
		const contentId = this.contentId;
		const [contentExposures] = await Promise.all([
			ContentExposureService.getContentExposures({ contentId, lessonPlanVersionId })
		]);
		this.formatContentExposures(contentExposures);
		this.setContentExposures(contentExposures);
	}

	@Action
	formatContentExposures(contentExposures: any[]) {
		const getImageUrl = (path: string) => {
			const pageSignature = this.signatures.find((s) => s.type === ContainerType.Page);

			if (pageSignature) {
				const splitedUrl = pageSignature.url.split("?");
				const signatureBase = splitedUrl[0] + pageSignature.name + "/";
				const signatureParams = "?" + splitedUrl[1];

				return signatureBase + path + signatureParams;
			}

			return path;
		};

		contentExposures.forEach((d) => {
			d.key = d.id;
			d.duration = { start: toTimeString(d.minPlanningTime), end: toTimeString(d.maxPlanningTime) };
			d.relatedItems = d.contentExposureRelatedItem.map((item) => ({
				contentId: item.contentId,
				type: item.type,
				title: item.content?.title || item.text,
				contentType: item.content?.contentType,
				vcsLevelId: item.content?.vcsLevelId,
				unitName: item.content?.vcsLevel?.displayName,
				unit: item.content?.vcsLevel?.unit,
				text: item.text,
				sequence: item.sequence,
				typeSequence: item.typeSequence
			}));
			d.teachingActivities = d.contentExposureTeachingActivity.map((item) => ({
				sequence: item.sequence,
				...item.teachingActivity,
				pageId: item.pageId,
				metaData: item.metaData,
				imageName: item.imageName,
				imageUrl: item.pageId && item.imageName ? getImageUrl(item.imageUrl) : null,
				objectiveId: item.objectiveId
			}));
			d.objectives = d.contentExposureObjective.map((item) => ({
				objectiveId: item.contentObjective.objective.id,
				text: item.contentObjective.objective.text,
				contentObjectiveId: item.contentObjectiveId,
				contentExposureId: item.contentExposureId,
				sequence: item.sequence
			}));
			d.mediaSlots = d.contentExposureMediaSlot.map((m) => m.mediaSlotId);
		});
	}

	@Action
	async getSignature() {
		const signature = await ContentExposureService.getTeachingActivitySas();
		this.setSignature(signature);
	}

	@Mutation
	setSignature(signature: SignatureResoureModel) {
		this.signatures = [...this.signatures, { ...signature }];
	}

	get getContentExposures() {
		return this.contentExposures;
	}

	get getVersionGroupId() {
		return this.versionGroupId;
	}

	get getVcsLevelId() {
		return this.vcsLevelId;
	}
}

export const ContentExposureModule = getModule(ContentExposureState);
